import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  teamListApi,
  teamMembersApi,
  teamFlowApi,
  taskTimelineApi,
  moveTaskApi,
  upsertTeamMemberApi,
  setTeamMemberManagerApi,
  designationsApi,
  setUserDesignationApi,
  setUserTeamsApi,
  teamWiseApi,
  upsertTeamApi,
  reorderTeamsApi,
} from "./teamApi";
import { showApiErrorToast } from "../../utils/toastErrorHandler";

/* These thunks deliberately do NOT clear the token on failure the way the
   task thunks do. A team lookup failing means the dropdown is empty, not that
   the session is gone — logging the user out over it would throw them back to
   the login screen mid-edit and lose the form they were filling in. */

export const teamListApis = createAsyncThunk(
  "team/list",
  async (params, { rejectWithValue }) => {
    try {
      return await teamListApi(params);
    } catch (err) {
      showApiErrorToast(err?.response?.data?.error || err.message);
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

/* Keyed by team in the slice: the Assign grid asks for several teams'
   members as the user moves between rows, and one shared list would make
   the last request win and show the wrong people. */
export const teamMembersApis = createAsyncThunk(
  "team/members",
  async (params, { rejectWithValue }) => {
    try {
      const res = await teamMembersApi(params);
      return { teamCode: params?.teamCode ?? "all", response: res };
    } catch (err) {
      showApiErrorToast(err?.response?.data?.error || err.message);
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const teamFlowApis = createAsyncThunk(
  "team/flow",
  async (params, { rejectWithValue }) => {
    try {
      const res = await teamFlowApi(params);
      return { fromTeamCode: params?.fromTeamCode ?? "all", response: res };
    } catch (err) {
      showApiErrorToast(err?.response?.data?.error || err.message);
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const teamWiseApis = createAsyncThunk(
  "team/teamWise",
  async (params, { rejectWithValue }) => {
    try {
      return await teamWiseApi(params);
    } catch (err) {
      showApiErrorToast(err?.response?.data?.error || err.message);
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const taskTimelineApis = createAsyncThunk(
  "team/timeline",
  async (params, { rejectWithValue }) => {
    try {
      return await taskTimelineApi(params);
    } catch (err) {
      showApiErrorToast(err?.response?.data?.error || err.message);
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);

export const moveTaskApis = createAsyncThunk(
  "team/move",
  async (payload, { rejectWithValue }) => {
    try {
      return await moveTaskApi(payload);
    } catch (err) {
      // moveTaskApi has already shown the server's reason.
      return rejectWithValue(err?.message || "Failed to move task");
    }
  },
);

export const setUserTeamsApis = createAsyncThunk(
  "team/userTeams",
  async (payload, { rejectWithValue }) => {
    try {
      return await setUserTeamsApi(payload);
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update teams");
    }
  },
);

/* The positions people can hold. Fetched once and kept in the slice: the
   roster dialog and the chart both label with it, and two components asking
   the same static question is two requests for one answer. */
export const designationsApis = createAsyncThunk(
  "team/designations",
  async (_, { rejectWithValue }) => {
    try {
      return await designationsApi();
    } catch (err) {
      return rejectWithValue(err?.message || "Could not load positions");
    }
  },
);

/* What one person does. On the person, not on their membership — so this
   changes what every team's chart calls them, which is the point. */
export const setUserDesignationApis = createAsyncThunk(
  "team/setDesignation",
  async (payload, { rejectWithValue }) => {
    try {
      return await setUserDesignationApi(payload);
    } catch (err) {
      const message = err?.error || err?.message || "Could not save the position";
      showApiErrorToast(message);
      return rejectWithValue(message);
    }
  },
);

/* Who somebody sits under, in one team.
 *
 * The rejection carries the server's own sentence through: the procedure
 * refuses a self-reference, a manager from another team and a cycle, each with
 * wording written for the person clicking, and a generic "failed" on top of
 * that would bury the only useful part. */
export const setTeamMemberManagerApis = createAsyncThunk(
  "team/memberManager",
  async (payload, { rejectWithValue }) => {
    try {
      return await setTeamMemberManagerApi(payload);
    } catch (err) {
      const message =
        err?.error || err?.message || "Could not save the reporting line";
      showApiErrorToast(message);
      return rejectWithValue(message);
    }
  },
);

export const upsertTeamMemberApis = createAsyncThunk(
  "team/member",
  async (payload, { rejectWithValue }) => {
    try {
      return await upsertTeamMemberApi(payload);
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to save team member");
    }
  },
);

/* Create a team, or rename one by passing teamCode.
 *
 * The rejection carries the server's message through untouched: the procedure
 * refuses duplicate names and short names with a sentence written for the
 * person typing, and replacing that with "Request failed" would throw away the
 * only part they can act on. */
export const upsertTeamApis = createAsyncThunk(
  "team/upsertTeam",
  async (payload, { rejectWithValue }) => {
    try {
      return await upsertTeamApi(payload);
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.response?.data?.message || err.message;
      showApiErrorToast(message);
      return rejectWithValue(message);
    }
  },
);

/* Save the left-to-right order of the teams.
   Takes { teamCodes: [...] } — the whole list, in the order wanted. */
export const reorderTeamsApis = createAsyncThunk(
  "team/reorder",
  async (payload, { rejectWithValue }) => {
    try {
      return await reorderTeamsApi(payload);
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.response?.data?.message || err.message;
      showApiErrorToast(message);
      return rejectWithValue(message);
    }
  },
);
